import M from '@materializecss/materialize';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logOut, registerRoutes } from '../../../func';
import { useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';
import Axios from 'axios';
import { useDispatch } from 'react-redux';

const ResolveManufacturerNamesModal = ({
  isOpen,
  setIsOpen,
  unknownManufacturerNames,
  setUnknownManufacturerNames,
  manufacturerList,
}) => {
  const authData = useMemo(
    () => JSON.parse(localStorage.getItem('auth_data')),
    []
  );
  const { path } = useRouteMatch();
  const dispatch = useDispatch();

  const modal = useRef(null);
  const autoComplete = useRef(null);
  const [value, setValue] = useState('');
  const [manufacturerNames, setManufacturerNames] = useState([]);
  const [loading, setLoading] = useState(false);

  const networkCalls = useMemo(
    () =>
      registerRoutes(
        {
          createAlias: {
            func: async (manufacturerId, alias) =>
              Axios.post('/api/v2/party/manufacturer/create/one/alias', {
                ...authData,
                manufacturerId,
                alias,
              }),
            type: 'c',
          },
        },
        path
      ),
    [path, authData]
  );

  useEffect(() => {
    const { current } = modal;
    if (current) {
      M.Modal.init(current, {
        dismissible: false,
        onCloseEnd: () => {
          setIsOpen(false);
        },
      });
      return () => {
        if (current) M.Modal.getInstance(current)?.destroy();
      };
    }
  }, [modal, setIsOpen]);

  useEffect(() => {
    const { current } = autoComplete;
    if (!current) return;
    const data = {};
    manufacturerNames.forEach(name => (data[name] = null));
    M.Autocomplete.init(current, {
      data,
      onAutocomplete: data => setValue(data),
    });
    return () => {
      M.Autocomplete.getInstance(current)?.destroy();
    };
  }, [autoComplete, unknownManufacturerNames, manufacturerNames]);

  useEffect(() => {
    if (!manufacturerList || !manufacturerList.length) return;
    setManufacturerNames(manufacturerList.map(({ name }) => name));
  }, [manufacturerList]);

  const getModal = useCallback(
    () => (modal.current ? M.Modal.getInstance(modal.current) : null),
    [modal]
  );
  const open = useCallback(() => getModal()?.open(), [getModal]);
  const close = useCallback(() => getModal()?.close(), [getModal]);

  useEffect(() => {
    isOpen ? open() : close();
  }, [isOpen, close, open]);

  const assignAlias = useCallback(async () => {
    try {
      setLoading(true);
      const alias = unknownManufacturerNames[0];
      const manufacturerId = manufacturerList.find(
        ({ name }) => name.toLocaleLowerCase() === value.toLocaleLowerCase()
      )?.id;

      const { data } = await networkCalls.createAlias(manufacturerId, alias);
      dispatch(data);
      const unknowns = [...unknownManufacturerNames];
      unknowns.shift();
      setUnknownManufacturerNames(unknowns);
      setValue('');

      if (unknowns.length < 1) close();
    } catch (err) {
      logOut(err);
    } finally {
      setLoading(false);
    }
  }, [
    value,
    unknownManufacturerNames,
    networkCalls,
    close,
    dispatch,
    manufacturerList,
    setUnknownManufacturerNames,
  ]);

  return (
    <div ref={modal} className="modal">
      <div className="modal-content">
        <div className="row">
          <h5 className="center">Resolve Manufacturer Names</h5>
          <small>{unknownManufacturerNames?.length} remaining</small>
          <div className="divider" />
        </div>
        {unknownManufacturerNames && unknownManufacturerNames.length && (
          <div className="row">
            <div className="col s6">
              <p style={{ fontSize: '1.3em' }}>{unknownManufacturerNames[0]}</p>
            </div>
            <div className="input-field col s6">
              <input
                ref={autoComplete}
                id="autocomplete"
                type="text"
                className="autocomplete"
                value={value}
                onChange={({ target: { value } }) => setValue(value)}
              />
              <label htmlFor="autocomplete">Manufacturer Name</label>
            </div>
          </div>
        )}
      </div>
      <div className="row">
        <div className="col s12 m4 offset-m8">
          <button
            className="btn-small blue white-text waves-effect waves-light col s12"
            onClick={assignAlias}
            disabled={!manufacturerNames?.includes(value) || loading}
          >
            Assign Alias
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResolveManufacturerNamesModal;
